import { __awaiter } from "tslib";
import { create } from 'zustand';
export const DefaultValues = {
    json: {},
    number: 0,
    boolean: false,
    string: ''
};
/**
 * `useSliceStore` is designed to manage slices of state within an application.
 * It expects descriptor, each of which represents a slice of the global state.
 * This allows each slice to be identified and managed independently. This setup supports various
 * external data sources such as localStorage, sessionStorage, or AsyncStorage in a generic and
 * agnostic manner, enabling seamless integration and synchronization with the application's global state.
 *
 * The store is designed to handle the asynchronous retrieval and updating of data through a defined
 * interface (`RetrievalFunctions`), which abstracts the actual data fetching and setting mechanisms.
 * This approach ensures that the data management logic is decoupled from the storage medium,
 * providing flexibility and scalability in managing state derived from different sources.
 *
 * Each slice's status is tracked to allow conditional operations based on its current state,
 * such as loading only when data is 'unset' or preventing redundant fetches, thus optimizing performance
 * and resource utilization.
 */
export const useSliceStore = create((set, get) => ({
    data: {},
    statuses: {},
    descriptorMap: new Map(),
    load: (descriptors) => __awaiter(void 0, void 0, void 0, function* () {
        const updates = {};
        const statusUpdates = {};
        yield Promise.all(descriptors.map((descriptor) => __awaiter(void 0, void 0, void 0, function* () {
            var _a;
            const namespace = (_a = get().descriptorMap.get(descriptor)) !== null && _a !== void 0 ? _a : `namespace-${Math.random().toString(36).slice(2, 11)}`;
            if (!get().descriptorMap.has(descriptor)) {
                get().descriptorMap.set(descriptor, namespace);
            }
            statusUpdates[namespace] = 'loading';
            const keys = Object.keys(descriptor.fields);
            const values = yield descriptor.sliceMethods.getItems(keys);
            const namespaceUpdates = {};
            keys.forEach(key => {
                var _a;
                const { type, defaultValue } = descriptor.fields[key];
                const value = (_a = values === null || values === void 0 ? void 0 : values[key]) !== null && _a !== void 0 ? _a : defaultValue; // Use default if value is undefined
                namespaceUpdates[key] = parseValue(value, type, defaultValue);
            });
            updates[namespace] = Object.assign(Object.assign({}, (get().data[namespace] || {})), namespaceUpdates);
        })));
        set(state => ({
            data: Object.assign(Object.assign({}, state.data), updates),
            statuses: Object.assign(Object.assign({}, state.statuses), statusUpdates)
        }));
    })
}));
export const parseValue = (rawValue, type, defaultValue = DefaultValues[type]) => {
    const processValue = (value) => {
        try {
            switch (type) {
                case 'json':
                    return JSON.parse(value || '{}');
                case 'number': {
                    const num = Number(value);
                    return isNaN(num) ? defaultValue : num;
                }
                case 'boolean':
                    return typeof value === 'boolean' ? value : value === 'true';
                case 'string':
                    return (value === null || value === void 0 ? void 0 : value.toString()) || '';
            }
        }
        catch (error) {
            console.error(`Error parsing the value: ${value}`, error);
            return defaultValue;
        }
    };
    return rawValue === undefined || rawValue === null ? defaultValue : processValue(rawValue);
};
//# sourceMappingURL=slice-store.js.map